<template>
  <div
    class="columns is-mobile is-multiline publication"
    :class="{
      'list': isListMode,
      'box-shadow': !isListMode,
      'featured': item.isFeatured
    }"
  >
    <div
      class="column is-12-mobile is-4-desktop"
      :class="{'is-hidden': isListMode, 'is-4-desktop': !isListMode}"
    >
      <div
        class="is-flex is-flex-direction-column is-justify-content-center"
      >
        <div
          class="pub-image card--img"
          :style="{ backgroundImage: getUrl(item.imagePath)}"
        >
          <div
            class="featured-wrapper"
          >
            <div
              v-if="item.isFeatured"
              class="featured-ribbon"
            >
              {{ $t("publication.list.featured") }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-show="!isListMode"
      class="column is-12-mobile"
      :class="{'is-8-desktop': !isListMode}"
    >
      <div
        class="is-flex is-flex-direction-column is-justify-content-center"
      >
        <p class="is-brightGreen">
          {{ item.displayDate }}
        </p>

        <a
          class="is-brightGreen"
          :href="detailedPage(item.id)"
          aria-label="visit detail page"
          target="_blank"
        >
          <div
            v-sane-html="highlightText(searchText, item.fullTitle)"
            class="h4 py-2"
          />
        </a>
        <p class="authors py-2">
          <strong> {{ $t("publication.list.author") }}: </strong>  <span
            v-for="(author, index) in item.authors"
            :key="'author' + index"
          >
            <a
              v-sane-html="highlightText(searchText, author)"
              :aria-label="'search author' + author"
              @click="searchAuthor(author)"
            >{{ author }}</a><template v-if="index < (item.authors?.length ?? 0 ) -1">, </template>
          </span>
        </p>

        <p
          v-sane-html="highlightText(searchText, item.description)"
          class="my-2 description"
        />
        <div class="link-wrap flex flex-wrap">
          <div>
            <hr class="mb-2 list-hidden">
          </div>
          <div>
            <a
              class="mb-2 button is-brightGreen is-inverted svg nav-arrow-green-15 right arrow"
              :href="detailedPage(item.id)"
              aria-label="view detail"
            >
              {{ $t("publication.list.detail") }} </a>
          </div>
          <div>
            <a
              v-if="item.fileName"
              download
              target="_blank"
              class="mb-2 ml-4 button is-brightGreen is-inverted svg nav-arrow-green-15 right arrow"
              :href="DOWNLOAD_URL + item.fileName"
              aria-label="download"
            >
              {{ $t("publication.list.download") }}</a>
          </div>
          <div>
            <a
              v-if="item.doi"
              target="_blank"
              class="mb-2 ml-4 button is-brightGreen is-inverted svg nav-arrow-green-15 right arrow"
              :href="getDOILink(item.doi ?? '')"
              aria-label="view doi page"
            > {{ $t("publication.list.view") }}</a>
          </div>
          <div>
            <hr class="mb-2 grid-hidden">
          </div>
        </div>
      </div>
    </div>
    <div
      v-show="isListMode"
      class="column is-12-mobile"
      :class="{'is-8-desktop': !isListMode}"
    >
      <div
        class="is-flex is-flex-direction-column is-justify-content-center"
      >
        <a
          class="is-brightGreen"
          :href="detailedPage(item.id)"
          target="_blank"
        >
          <div>
            <div
              v-if="item.isFeatured"
              class="featured-wrapper"
            >
              <div

                class="featured-ribbon list-feature-ribbon"
              >
                {{ $t("publication.list.featured") }}
              </div>
            </div>
            <div
              v-sane-html="highlightText(searchText, item.fullTitle)"
              class="h4 pt-2 pb-0"
            />
          </div>
        </a>

        <p
          v-sane-html="highlightText(searchText, item.description)"
          class="my-2 description"
        />
        <div class="list-meta-flex">
          <div>
            <strong>{{ $t("publication.list.publishDate") }}: </strong>
            <span class="is-brightGreen">
              {{ item.displayDate }}
            </span>
          </div>
          <div class="authors py-2">
            <strong> {{ $t("publication.list.author") }}: </strong>  <span
              v-for="(author, index) in item.authors"
              :key="'author' + index"
            >
              <a
                v-sane-html="highlightText(searchText, author)"
                @click="searchAuthor(author)"
              >{{ author }}</a><template v-if="index < (item.authors?.length ?? 0 ) -1">, </template>
            </span>
          </div>
        </div>
        <div
          v-if="false"
          class="link-wrap flex flex-wrap"
        >
          <div>
            <hr class="mb-2 list-hidden">
          </div>
          <div>
            <a
              class="mb-2 button is-brightGreen is-inverted svg nav-arrow-green-15 right arrow"
              :href="detailedPage(item.id)"
            >
              {{ $t("publication.list.detail") }} </a>
          </div>
          <div>
            <a
              v-if="item.fileName"
              download
              target="_blank"
              class="mb-2 ml-4 button is-brightGreen is-inverted svg nav-arrow-green-15 right arrow"
              :href="DOWNLOAD_URL + item.fileName"
            >
              {{ $t("publication.list.download") }}</a>
          </div>
          <div>
            <a
              v-if="item.doi != null"
              target="_blank"
              class="mb-2 ml-4 button is-brightGreen is-inverted svg nav-arrow-green-15 right arrow"
              :href="getDOILink(item.doi ?? '')"
            > {{ $t("publication.list.view") }}</a>
          </div>
          <div>
            <hr class="mb-2 grid-hidden">
          </div>
        </div>

        <hr
          v-if="!isLast"
          class="separator mt-4 -mb-5"
        >
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { PublicationItem } from '@/composables/state/publicationSearchApp';

import { PropType } from 'vue';
import { highlightText } from '@/config/common';

const props = defineProps({
  item: { type: Object as PropType<PublicationItem>, default: () => { return {}; } },
  defaultImageUrl: { type: String, default: '' },
  childPageUrl: { type: String, default: '' },
  searchText: { type: String as PropType<string | null>, default: '' },
  isListMode: { type: Boolean, default: false },
  isLast: { type: Boolean, default: false },
});

const getUrl = (imgUrl:string | undefined) => {
  return (imgUrl != null && imgUrl.length > 0) ? `url("/.imaging/mte/abmi-theme/max-400/dam${imgUrl}")` : `url("${props.defaultImageUrl}")`;
};

const detailedPage = (itemId: string | undefined) => {
  return props.childPageUrl + itemId + '.html';
};

const getDOILink = (link: string) => {
  if (link == null || link.length === 0) {
    return '';
  }
  if (link.startsWith('http')) {
    return link;
  }
  // https:// doi.org/
  if (link.startsWith('//doi.org')) {
    return link;
  }

  return 'https://doi.org/' + link;
};
const searchAuthor = (author:string) => {
  window.location.href =
        window.location.protocol + '//' +
        window.location.host +
        window.location.pathname +
        '?author=' + author;
};
// const WEB_HOME_URL = import.meta.env.VITE_APP_HOME_URL ?? '';
const DOWNLOAD_URL = import.meta.env.VITE_APP_PUBLICATION_DOWNLOAD_URL_PREFIX ?? '';

</script>

<style scoped>
.description {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.list .description {
  -webkit-line-clamp: 2;
}
.pub-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  min-height:250px;
  background-position: center;
}

.box-shadow.featured {
  box-shadow: 0 4px 30px #3a26263b;
  border: 2px solid #d4d5d1;
}

.list-feature-ribbon {
  border-radius: 1rem;
  padding: 0 1em;
  margin-inline-start: -0.25em;
  margin-bottom: 0.25lh;
  align-self: flex-start;
  font-size: .8rem !important;
}

@media screen and (min-width: 1024px) {
  .list-meta-flex {
    display: flex;
    align-items: center;
    gap: 1.5em;
    margin-bottom: -0.5lh;
  }
}

.featured-wrapper {
  position: relative;
}
</style>
